import { useParams } from "react-router-dom";
import { useOne, useTranslate, useGo, Authenticated } from "@refinedev/core";
import { Card, Flex, Result, Spin } from "antd";
import { ProjectSettingForm } from "./components/ProjectSettingForm";
import { MediaProjectResponse } from "pages/media/types";
import {
  TargetIcon,
  UsersThreeIcon,
  SwatchesIcon,
} from "@phosphor-icons/react";
import { SubPageHeader } from "../../../components/SubPageHeader";
import { useProjectNavigate } from "./hooks/useProjectNavigate";

const SETTINGS = {
  brandkit: {
    type: "BrandKit",
    icon: <SwatchesIcon size={32} weight="duotone" />,
    getTitle: (t) => t("projects.components.ProjectCreateWizard.BrandKit"),
    getSubtitle: (t) =>
      t("projects.components.ProjectSettings.chooseYourBrand"),
  },
  contact: {
    type: "Contacts",
    icon: <UsersThreeIcon size={32} weight="duotone" />,
    getTitle: (t) => t("projects.components.ProjectSettings.contacts"),
    getSubtitle: (t, isDraft) => (
      <>
        {t("projects.components.ProjectSettings.chooseTheContacts")}
        {isDraft && (
          <>
            <br />
            {t("projects.components.ProjectSettings.youCanAddMultiple")}
          </>
        )}
      </>
    ),
  },
  ctas: {
    type: "CallToActions",
    icon: <TargetIcon size={32} weight="duotone" />,
    getTitle: (t) => t("projects.components.ProjectSettings.callToActions"),
    getSubtitle: (t, _, go) => (
      <>{t("projects.components.ProjectSettings.pickAnExisting")}</>
    ),
  },
} as const;

export const ProjectSettingsPage = () => {
  const { id, setting, storyboardId, instanceId } = useParams();
  const t = useTranslate();
  const go = useGo();
  const { navigateToProject } = useProjectNavigate();

  const { data: projectData, isLoading } = useOne<MediaProjectResponse>({
    resource: "media/projects",
    id: id,
    queryOptions: { enabled: !!id },
  });
  const instanceSettings = projectData?.data?.media?.find(
    (x) => x.instance_id === instanceId
  );
  const storyboard = projectData?.data?.storyboards?.find(
    (x) => x.id === storyboardId
  );
  const config = setting ? SETTINGS[setting as keyof typeof SETTINGS] : null;

  if (!config) {
    return <Result status={404} title="Invalid settings" />;
  }

  const isDraft = storyboard?.status === "WaitingUserInput";
  let value;
  if (config.type === "Contacts") {
    value = instanceSettings?.contact_id;
  } else if (config.type === "BrandKit") {
    value = instanceSettings?.brand_kit_id;
  } else if (config.type === "CallToActions") {
    value = instanceSettings?.cta_group_id;
  }

  return (
    <Authenticated key="project_settings">
      {projectData && (
        <SubPageHeader
          itemId={projectData.data.id}
          itemTitle={projectData.data.title}
          page={config.type}
          source="projects"
        />
      )}
      {isLoading && <Spin fullscreen />}
      {!isLoading && (
        <Flex
          vertical
          justify="center"
          align="center"
          style={{
            padding: "2rem",
          }}
        >
          <Card style={{ width: "100%" }}>
            <ProjectSettingForm
              type={config.type}
              icon={config.icon}
              title={config.getTitle(t)}
              subtitle={config.getSubtitle(t, isDraft, go)}
              projectId={id}
              storyboardId={storyboardId}
              instanceId={instanceId}
              value={value}
              onClose={() =>
                navigateToProject({
                  projectId: id,
                  storyboardId,
                })
              }
              cancellable={true}
            />
          </Card>
        </Flex>
      )}
    </Authenticated>
  );
};
